import { useTableFilterConditions } from '@smartsash/data-screen';
import { TableFilter, useTableFilterFields } from '@smartsash/ui';
import type { TableColumn, TableFilterFieldOverrides } from '@smartsash/ui';

/**
 * The designed filter builder as a data-screen `filterSlot` (HS-869).
 *
 * `defineDataScreen` hands `filterSlot` the route search and nothing else, so each entity's filter
 * component resolves its own rows — a cache read of the same query key the screen renders — and
 * hands them here with the columns to derive fields from. This part is identical for every entity,
 * so it lives once.
 *
 * The builder itself, its field deriver and the URL codec now live in `@smartsash/ui` and
 * `@smartsash/data-screen` (HS-1440); what stays here is admin's data-screen wiring.
 */
export function DataScreenFilter<T>({
  columns,
  rows,
  overrides,
}: {
  columns: TableColumn<T>[];
  rows: T[];
  overrides?: TableFilterFieldOverrides;
}) {
  const fields = useTableFilterFields(columns, rows, overrides);
  const { conditions, apply, clear } = useTableFilterConditions();

  return <TableFilter fields={fields} conditions={conditions} onApply={apply} onClear={clear} />;
}

/**
 * Turn an entity's column-text map into the columns the field deriver reads.
 *
 * `useTableFilterFields` wants a header for copy and a `value` for the vocabulary; it never renders
 * a cell, hence the empty renderer. Building the list from the same text map the table uses is what
 * keeps a filter field and its column from drifting apart.
 */
export function filterColumnsFromText<T>(
  text: Record<string, (row: T) => string>,
  label: (id: string) => string,
): TableColumn<T>[] {
  return Object.entries(text).map(([id, value]) => ({
    id,
    header: label(id),
    cell: () => null,
    value,
  }));
}
